import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import {DocumentNode} from 'graphql';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {exportAuditLogs} from 'audit/AuditActions';
import {EXPORT_AUDIT_LOG_DISPLAY_STRING} from 'audit/AuditConstants';
import {pathToURL} from 'BenchlingUtil';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {
  CachedDataWhenRefetchingNamedOption,
  QueryResultType,
  useSkippableBenchlingQuery,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible, accessibleObjs} from 'graphql-client/InaccessibleInfo';
import ToastMessage from 'nav/toasts/ToastMessage';
import notifications from 'notifications';
import ErrorBadge, {ErrorBadgeError} from 'procedures/design/error-panel/ErrorBadge';
import {promoteProcedureRunPlans} from 'procedures/network/procedureRunPlanDAO';
import {
  getPowAgnosticProcedureFromFlowchartVersion,
  getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan,
} from 'procedures/utils/powAgnosticFlowchartHelpers';
import {BenchlingState} from 'redux-stores/reduceApp';
import {
  ProcedureRunTab,
  type ProcedureRunOrPlanStateSummary,
  setSelectedProcedureRun,
  setSelectedProcedureRunPlan,
  setCurrentProcedureRunTab as setCurrentProcedureRunTabAction,
  addPromotingPlanId as addPromotingPlanIdAction,
  removePromotingPlanId as removePromotingPlanIdAction,
  resetStudyState,
} from 'studies/redux/ProcedureRunPlanUIReduxActions';
import {
  getExperimentUnknownErrorAction,
  onSuccessAfterExperimentCreate,
  showExperimentCreationTrayTask,
} from 'studies/sidepanel/ExperimentCreationTrayTask';
import {navigateToStudyOverviewTab} from 'studies/StudyNavigationUtils';
import {ProcedureRunSelectionForAnalysis} from 'studies/typed/shared/analysis/useProcedureRunSelectionForAnalysis';
import {getProcedureRunUIStringForStudyType, getProcedureTypeForStudyType} from 'studies/TypedStudyUtils';
import {StudyOverviewViewQuery} from 'studies/workspace/overview/__generated/StudyOverviewViewQuery';
import SetStudyPhaseDropdown from 'studies/workspace/overview/SetStudyPhaseDropdown';
import {FONT_PROXIMA_12} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import * as TrayTaskActions from 'tray/TrayTaskActions';
import * as TrayTaskConstants from 'tray/TrayTaskConstants';
import Breadcrumbs, {CrumbLink} from 'ui-library/Breadcrumbs';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import * as Colors from 'ui-library/Colors';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useOnMount from 'ui-library/hooks/useOnMount';
import {CenteredSpinner} from 'ui-library/Spinner';
import copyToClipboard from 'util/clipboard/copyToClipboard';
import {getPromotingPlanIdsForStudy} from 'util/resume-persisted-tasks/ResumePersistedTasks';

import {StudyOverviewTabContentsProps} from '../../../utils/TypedStudyTypes';
import AddProcedureRunPlanButton from '../../../utils/ui/AddProcedureRunPlanButton';
import AnalyzeProcedureRunButton from '../../../utils/ui/AnalyzeProcedureRunButton';
import AnalyzeStudyButton from '../../../utils/ui/AnalyzeStudyButton';
import LinkToExistingUnitOperationsButton from '../../../utils/ui/LinkToExistingUnitOperationsButton';
import LinkToExistingUnitOperationsPlanButton from '../../../utils/ui/LinkToExistingUnitOperationsPlanButton';
import {shouldShowFlowchartProcedureRunDesign} from '../design-tabs/ProcedureRunDesignUtils';
import {getProcedureRunPlanName} from '../design-tabs/ProcedureRunPlanUtils';
import {
  MultiProcedureRunOnlyOverviewQuery,
  MultiProcedureRunOnlyOverviewQueryVariables,
} from './__generated/MultiProcedureRunOnlyOverviewQuery';
import {
  MultiProcedureRunPlanOverviewQuery,
  MultiProcedureRunPlanOverviewQueryVariables,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode as ProcedureRunPlanNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode as TemplateProcedureFlowchartVersionNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_procedureFlowchartConfigVersionProxy_ProcedureFlowchartConfigVersionProxyNode as ProcedureFlowchartConfigVersionProxyNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_procedureFlowchartConfigVersionProxy_ProcedureFlowchartConfigVersionProxyNode_procedure_ProcedureNode as FlowchartProxyProcedureNode,
  MultiProcedureRunPlanOverviewQuery_procedureRunPlan_ProcedureRunPlanNode_templateProcedureFlowchartVersion_ProcedureFlowchartVersionNode_procedureFlowchart_ProcedureFlowchartNode_parent_ProcedureNode as TemplateFlowchartProcedureNode,
} from './__generated/MultiProcedureRunPlanOverviewQuery';
import ConfirmPromotionModal from './ConfirmPromotionModal';
import ProcedureRunHeader from './ProcedureRunHeader';
import ProcedureRunLayout from './ProcedureRunLayout';
import ProcedureRunPlanHeader from './ProcedureRunPlanHeader';
import StudyExecutionHeader, {StudyExecutionHeaderTabsProps} from './StudyExecutionHeader';
import {
  ProcedureRunContext,
  ProcedureRunPlanContext,
  StudyOverviewContext,
  type PlanDesignActions,
  type RunDesignActions,
} from './StudyOverviewContexts';

type StudyNode = ExtractByTypename<StudyOverviewViewQuery['study'], 'StudyNode'>;
type PowAgnosticTemplateFlowchartVersion =
  | ProcedureFlowchartConfigVersionProxyNode
  | TemplateProcedureFlowchartVersionNode;
type ProcedureFromFlowchartVersion = FlowchartProxyProcedureNode | TemplateFlowchartProcedureNode;

export {ProcedureRunTab} from 'studies/redux/ProcedureRunPlanUIReduxActions';
export type {ProcedureRunOrPlanStateSummary} from 'studies/redux/ProcedureRunPlanUIReduxActions';

/*
 * Exported for testing
 */
export function studyOverviewCrumbs(props: {
  selectedProcedureRun: ProcedureRunOrPlanStateSummary;
  selectedProcedureRunPlan: ProcedureRunOrPlanStateSummary;
  studyName: string;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  studyId: string;
}): JSX.Element {
  return (
    <>
      <div {...getTestProps({component: 'MultiProcedureRunOverviewTabContents', element: 'breadcrumbs'})}>
        <Breadcrumbs>
          <CrumbLink
            onClick={() => {
              // resets the procedure run tab to overview when selecting a new procedure run or design when selecting a new procedure run plan
              props.setCurrentProcedureRunTab(
                props.selectedProcedureRunPlan.id != null ? ProcedureRunTab.DESIGN : ProcedureRunTab.OVERVIEW
              );
              navigateToStudyOverviewTab(props.studyId);
            }}
          >
            {props.studyName}
          </CrumbLink>
        </Breadcrumbs>
      </div>
      {props.selectedProcedureRun.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRun.name}</div>
      )}
      {props.selectedProcedureRunPlan.name && (
        <div className={css(styles.breadcrumbsFont)}>{props.selectedProcedureRunPlan.name}</div>
      )}
    </>
  );
}

/*
 * Exported for testing
 */
export function procedureRunViewerTabsProps({
  currentProcedureRunTab,
  setCurrentProcedureRunTab,
  selectedProcedureRunId,
  selectedProcedureRunPlanId,
}: {
  currentProcedureRunTab: ProcedureRunTab;
  setCurrentProcedureRunTab: (procedureRunTab: ProcedureRunTab) => void;
  selectedProcedureRunId: string | null;
  selectedProcedureRunPlanId: string | null;
}): StudyExecutionHeaderTabsProps<ProcedureRunTab> {
  const headerTabsProps = [{tabKey: ProcedureRunTab.DESIGN, title: 'DESIGN'}];
  if (selectedProcedureRunId != null) {
    // adds the overview tab to the beginning of the tabs array
    headerTabsProps.unshift({tabKey: ProcedureRunTab.OVERVIEW, title: 'RUN OVERVIEW'});
  }
  if (selectedProcedureRunPlanId != null || selectedProcedureRunId != null) {
    // Fall back to the first available tab if the current tab isn't valid for this view.
    // This prevents a TabContainer crash when transitioning between run/plan/listing views
    // without re-mounting (e.g., plans only have DESIGN, not OVERVIEW).
    const activeTab = headerTabsProps.some((tab) => tab.tabKey === currentProcedureRunTab)
      ? currentProcedureRunTab
      : headerTabsProps[0].tabKey;
    const headerTabContainerProps = {
      activeTab,
      className: css(styles.tabContainer),
      headerDividerClassName: css(styles.headerDivider),
      isFullHeight: true,
      navClassName: css(styles.tabList),
      onRequestTabChange: setCurrentProcedureRunTab,
      paneClassName: css(styles.tabPanel),
      tabClassName: css(styles.tab),
    };
    return {
      headerTabContainerProps,
      headerTabsProps,
    };
  }
  return {};
}

export const MULTI_PROCEDURE_RUN_ONLY_OVERVIEW_QUERY: DocumentNode = gql`
  query MultiProcedureRunOnlyOverviewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRun(id: $id) {
      ... on ProcedureRunNode {
        id
        name
        createdAt
        description
        archiveData {
          archived
        }
        procedureRunPlan {
          ... on ProcedureRunPlanNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const MULTI_PROCEDURE_RUN_PLAN_OVERVIEW_QUERY: DocumentNode = gql`
  query MultiProcedureRunPlanOverviewQuery($id: StrictString!) @skipAudit @engComponent(name: STUDIES) {
    procedureRunPlan(id: $id) {
      ... on ProcedureRunPlanNode {
        id
        createdAt
        description
        procedureRun {
          ... on ProcedureRunNode {
            id
          }
          ...InaccessibleInfoGraphQL
        }
        templateProcedureFlowchartVersion {
          ... on ProcedureFlowchartVersionNode {
            id
            procedureFlowchart {
              ... on ProcedureFlowchartNode {
                id
                parent {
                  ... on ProcedureNode {
                    id
                    name
                  }
                }
              }
            }
            migratedToFlowchartConfigVersionProxy {
              ... on ProcedureFlowchartConfigVersionProxyNode {
                id
              }
              ...InaccessibleInfoGraphQL
            }
          }
          ...InaccessibleInfoGraphQL
        }
        procedureFlowchartConfigVersionProxy {
          ... on ProcedureFlowchartConfigVersionProxyNode {
            id
            procedure {
              ... on ProcedureNode {
                id
                name
              }
            }
          }
          ...InaccessibleInfoGraphQL
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

interface ProcedureRunOrPlanViewButtonsProps {
  study: StudyNode;
  selectedProcedureRunId?: string;
  selectedProcedureRunPlanId?: string;
  shouldDisableCreate?: boolean;
  isProcedureRunArchived: boolean;
  planOrRunName: string;
  onLinksSaved?: () => void;
  onOpenPromotePlanConfirmation?: () => void;
  planErrors?: Array<ErrorBadgeError>;
  hasUnsavedPlanChanges?: boolean;
}

function CopyLinkButton({study, id}: {study: StudyNode; id: string}): JSX.Element {
  const procedureRunLabel = getProcedureRunUIStringForStudyType(study.studySchema.studyType);
  const copyProcedureRunLinkText = `Copy ${procedureRunLabel} link`;

  return (
    <Tooltip text={copyProcedureRunLinkText}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        size={ButtonSize.MEDIUM}
        isDropdown={false}
        onClick={() =>
          copyToClipboard(pathToURL(`${study.editUrl}?runId=${id}`), {
            showToastOnSuccess: true,
            toastText: `Copied ${procedureRunLabel} link.`,
          })
        }
        icon="link"
        aria-label={copyProcedureRunLinkText}
      />
    </Tooltip>
  );
}

function ExportAuditLogButton({auditLogIds}: {auditLogIds: Array<string>}): JSX.Element {
  return (
    <Tooltip text="Export audit log">
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        icon="audit-trail"
        size={ButtonSize.MEDIUM}
        onClick={() => {
          exportAuditLogs(auditLogIds);
        }}
        aria-label={EXPORT_AUDIT_LOG_DISPLAY_STRING}
      />
    </Tooltip>
  );
}

function ProcedureRunButtons({
  study,
  procedureRunId,
  planOrRunName,
  isProcedureRunArchived,
}: {
  study: StudyNode;
  procedureRunId: string;
  planOrRunName: string;
  isProcedureRunArchived: boolean;
}): JSX.Element {
  return (
    <>
      <LinkToExistingUnitOperationsButton
        studyId={study.id}
        procedureRunId={procedureRunId}
        procedureRunName={planOrRunName}
        studyType={study.studySchema.studyType}
      />
      <AnalyzeProcedureRunButton
        study={{id: study.id, name: study.name, humanId: study.humanId}}
        isStudyArchived={study.archiveRecord != null}
        studyType={study.studySchema.studyType}
        procedureRunId={procedureRunId}
        procedureRunName={planOrRunName}
        isProcedureRunArchived={isProcedureRunArchived}
        procedureType={getProcedureTypeForStudyType(study.studySchema.studyType)}
        actionOrigin={ActionOrigin.STUDY_RUN_DETAILS_PAGE}
      />
    </>
  );
}

function ProcedureRunPlanButtons({
  study,
  procedureRunPlanId,
  planOrRunName,
  onLinksSaved,
  onOpenPromotePlanConfirmation,
  planErrors,
  hasUnsavedPlanChanges,
  shouldDisableCreate,
}: {
  study: StudyNode;
  procedureRunPlanId: string;
  planOrRunName: string;
  onLinksSaved?: () => void;
  onOpenPromotePlanConfirmation?: () => void;
  planErrors?: Array<ErrorBadgeError>;
  hasUnsavedPlanChanges?: boolean;
  shouldDisableCreate?: boolean;
}): JSX.Element {
  const hasPlanErrors = planErrors != null && planErrors.length > 0;
  return (
    <>
      <LinkToExistingUnitOperationsPlanButton
        studyId={study.id}
        procedureRunPlanId={procedureRunPlanId}
        procedureRunPlanName={planOrRunName}
        studyType={study.studySchema.studyType}
        onLinksSaved={onLinksSaved ?? (() => {})}
      />
      <Button
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        size={ButtonSize.MEDIUM}
        text={hasUnsavedPlanChanges ? 'Save and create run' : 'Create run'}
        disabled={hasPlanErrors || shouldDisableCreate}
        onClick={onOpenPromotePlanConfirmation}
        dataTestAttrs={{component: 'MultiProcedureRunOverviewTabContents', element: 'createButton'}}
      />
    </>
  );
}

function getProcedureRunOrPlanViewButtons({
  study,
  selectedProcedureRunId,
  selectedProcedureRunPlanId,
  isProcedureRunArchived,
  planOrRunName,
  onLinksSaved,
  onOpenPromotePlanConfirmation,
  planErrors,
  hasUnsavedPlanChanges,
  shouldDisableCreate,
}: ProcedureRunOrPlanViewButtonsProps): JSX.Element {
  assert(
    selectedProcedureRunId !== undefined || selectedProcedureRunPlanId !== undefined,
    'Expected either a procedure run or procedure run plan to be selected.'
  );
  assert(planOrRunName !== '', 'Expected a plan or run name to be provided.');

  const hasPlanErrors = planErrors != null && planErrors.length > 0;
  const linkId = selectedProcedureRunId ?? selectedProcedureRunPlanId!;

  // Collect IDs for audit log export. When viewing a procedure run promoted from a plan, both IDs are
  // included.
  const auditLogIds = [selectedProcedureRunId, selectedProcedureRunPlanId].filter(
    (id): id is string => id != null
  );

  return (
    <>
      {selectedProcedureRunPlanId != null && hasPlanErrors && (
        <ErrorBadge
          errors={planErrors}
          title={`Fix all errors to create this ${getProcedureRunUIStringForStudyType(study.studySchema.studyType)}`}
        />
      )}
      <CopyLinkButton study={study} id={linkId} />
      {selectedProcedureRunId != null && (
        <ProcedureRunButtons
          study={study}
          procedureRunId={selectedProcedureRunId}
          planOrRunName={planOrRunName}
          isProcedureRunArchived={isProcedureRunArchived}
        />
      )}
      {selectedProcedureRunId == null && selectedProcedureRunPlanId != null && (
        <ProcedureRunPlanButtons
          study={study}
          procedureRunPlanId={selectedProcedureRunPlanId}
          planOrRunName={planOrRunName}
          onLinksSaved={onLinksSaved}
          onOpenPromotePlanConfirmation={onOpenPromotePlanConfirmation}
          planErrors={planErrors}
          hasUnsavedPlanChanges={hasUnsavedPlanChanges}
          shouldDisableCreate={shouldDisableCreate}
        />
      )}
      <ExportAuditLogButton auditLogIds={auditLogIds} />
    </>
  );
}

const EMPTY_PROMOTING_PLAN_IDS: Array<string> = [];

export default function MultiProcedureRunOverviewTabContents(
  props: StudyOverviewTabContentsProps
): JSX.Element {
  const dispatch = useDispatch();
  const studyId = props.study.id;
  const defaultTab = props.runPlanId != null ? ProcedureRunTab.DESIGN : ProcedureRunTab.OVERVIEW;

  // Get Redux state for this study using stable selectors
  const reduxCurrentTab = useSelector(
    (state: BenchlingState) => state.ui.procedureRunPlans.byStudyId[studyId]?.currentProcedureRunTab
  );
  const currentProcedureRunTab = reduxCurrentTab ?? defaultTab;

  const reduxPromotingPlanIds = useSelector(
    (state: BenchlingState) =>
      state.ui.procedureRunPlans.byStudyId[studyId]?.promotingPlanIds ?? EMPTY_PROMOTING_PLAN_IDS
  );

  // Merge persisted task promoting IDs with Redux state on mount only
  const hasInitializedPromotingPlanIds = useRef(false);
  useEffect(() => {
    if (!hasInitializedPromotingPlanIds.current) {
      hasInitializedPromotingPlanIds.current = true;
      const persistedPlanIds = getPromotingPlanIdsForStudy(studyId);
      persistedPlanIds.forEach((planId) => {
        dispatch(addPromotingPlanIdAction(studyId, planId));
      });
    }
  }, [dispatch, studyId]);

  // Clear per-study procedure-run/plan UI state only when the browser page is being exited.
  // This preserves state across internal Benchling tab switches while preventing stale carry-over
  // across full tab/page exits.
  useEffect(() => {
    const clearStudyStateOnPageExit = (): void => {
      dispatch(resetStudyState(studyId));
    };

    window.addEventListener('pagehide', clearStudyStateOnPageExit);
    window.addEventListener('beforeunload', clearStudyStateOnPageExit);

    return () => {
      window.removeEventListener('pagehide', clearStudyStateOnPageExit);
      window.removeEventListener('beforeunload', clearStudyStateOnPageExit);
    };
  }, [dispatch, studyId]);

  // Convert promotingPlanIds array to Set for backwards compatibility with existing code
  const promotingPlanIds = useMemo(() => new Set(reduxPromotingPlanIds), [reduxPromotingPlanIds]);

  const procedureRunResult = useSkippableBenchlingQuery<
    MultiProcedureRunOnlyOverviewQuery,
    MultiProcedureRunOnlyOverviewQueryVariables
  >(MULTI_PROCEDURE_RUN_ONLY_OVERVIEW_QUERY, {
    variables: {id: props.runId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
    skip: props.runId == null,
  });
  const procedureRunPlanResult = useSkippableBenchlingQuery<
    MultiProcedureRunPlanOverviewQuery,
    MultiProcedureRunPlanOverviewQueryVariables
  >(MULTI_PROCEDURE_RUN_PLAN_OVERVIEW_QUERY, {
    variables: {id: props.runPlanId!},
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    renderCachedDataWhenRefetching: CachedDataWhenRefetchingNamedOption.WHEN_LOADING,
    skip: props.runPlanId == null,
  });
  let fetchedProcedureRun: Extract<
    MultiProcedureRunOnlyOverviewQuery['procedureRun'],
    {__typename: 'ProcedureRunNode'}
  > | null = null;
  let fetchedProcedureRunPlan: ProcedureRunPlanNode | null = null;
  let fetchedTemplateFlowchartVersion: PowAgnosticTemplateFlowchartVersion | null = null;
  let procedureRunPlanIdFromRun: string | null = null;
  // if a user navigates to the procedure run/plan view via Url, we only show the procedure run/plan view if
  // the run/plan ID is valid and if the user has permission to view the run/plan. Otherwise, we navigate to
  // the procedure run/plan listing view
  if (
    procedureRunResult.type === QueryResultType.SUCCESS &&
    procedureRunResult.data &&
    !isInaccessible(procedureRunResult.data.procedureRun)
  ) {
    fetchedProcedureRun = procedureRunResult.data.procedureRun;
    // Extract procedure run plan ID from the procedure run if it exists
    if (
      fetchedProcedureRun.procedureRunPlan != null &&
      !isInaccessible(fetchedProcedureRun.procedureRunPlan)
    ) {
      procedureRunPlanIdFromRun = fetchedProcedureRun.procedureRunPlan.id;
    }
  }
  if (
    procedureRunPlanResult.type === QueryResultType.SUCCESS &&
    procedureRunPlanResult.data &&
    !isInaccessible(procedureRunPlanResult.data.procedureRunPlan)
  ) {
    fetchedProcedureRunPlan = procedureRunPlanResult.data.procedureRunPlan;
    const flowchartVersion =
      getPowAgnosticTemplateFlowchartVersionFromProcedureRunPlan(fetchedProcedureRunPlan);
    if (flowchartVersion != null && !isInaccessible(flowchartVersion)) {
      fetchedTemplateFlowchartVersion = flowchartVersion;
    }
  }
  const selectedProcedureRun = useMemo(() => {
    if (fetchedProcedureRun != null) {
      return {id: fetchedProcedureRun.id, name: fetchedProcedureRun.name};
    }
    return {id: null, name: null};
  }, [fetchedProcedureRun]);

  const selectedProcedureForPlan: ProcedureFromFlowchartVersion | null =
    fetchedTemplateFlowchartVersion != null
      ? getPowAgnosticProcedureFromFlowchartVersion(fetchedTemplateFlowchartVersion)
      : null;
  const selectedProcedureRunPlan = useMemo(() => {
    if (fetchedProcedureRunPlan != null) {
      const name = getProcedureRunPlanName(fetchedProcedureRunPlan);
      return {
        id: fetchedProcedureRunPlan.id,
        name,
        procedureId: selectedProcedureForPlan?.id ?? null,
      };
    }
    return {id: null, name: null, procedureId: null};
  }, [fetchedProcedureRunPlan, selectedProcedureForPlan]);
  assert(
    selectedProcedureRun || selectedProcedureRunPlan,
    'Expected either a procedure run or procedure run plan to be selected.'
  );

  // Sync selected procedure run to Redux when fetched
  useEffect(() => {
    if (selectedProcedureRun.id != null) {
      dispatch(setSelectedProcedureRun(studyId, selectedProcedureRun));
    }
  }, [dispatch, selectedProcedureRun, studyId]);

  // Sync selected procedure run plan to Redux when fetched
  useEffect(() => {
    if (selectedProcedureRunPlan.id != null) {
      dispatch(setSelectedProcedureRunPlan(studyId, selectedProcedureRunPlan));
    }
  }, [dispatch, selectedProcedureRunPlan, studyId]);

  // Callback to set current procedure run tab (dispatches to Redux)
  const setCurrentProcedureRunTab = useCallback(
    (tab: ProcedureRunTab) => {
      dispatch(setCurrentProcedureRunTabAction(studyId, tab));
    },
    [dispatch, studyId]
  );

  // If the user navigates directly to a plan that is actively being promoted, redirect
  // them to the study overview listing page instead of showing the stale plan detail view.
  // We check both Redux state and persisted tasks to handle the case where the component
  // is mounted before Redux state is initialized.
  useOnMount(() => {
    if (props.runPlanId != null) {
      const persistedPromotingIds = getPromotingPlanIdsForStudy(studyId);
      if (promotingPlanIds.has(props.runPlanId) || persistedPromotingIds.has(props.runPlanId)) {
        navigateToStudyOverviewTab(studyId);
      }
    }
  });

  // If the user navigates to a plan that has already been promoted to a procedure run,
  // redirect them to the resulting procedure run and show an informational toast.
  const procedureRunIdFromPlan =
    fetchedProcedureRunPlan?.procedureRun != null && !isInaccessible(fetchedProcedureRunPlan.procedureRun)
      ? fetchedProcedureRunPlan.procedureRun.id
      : null;
  useEffect(() => {
    if (procedureRunIdFromPlan != null) {
      const label = getProcedureRunUIStringForStudyType(props.study.studySchema.studyType);
      notifications.info(`This plan has already been promoted to a ${label}.`);
      navigateToStudyOverviewTab(props.study.id, procedureRunIdFromPlan);
    }
  }, [procedureRunIdFromPlan, props.study.id, props.study.studySchema.studyType]);

  const [isConfirmPromotionModalOpen, setIsConfirmPromotionModalOpen] = useState(false);

  const [planDesignActions, setPlanDesignActions] = useState<PlanDesignActions | null>(null);
  const hasUnsavedPlanChanges = planDesignActions?.hasUnsavedChanges ?? false;
  const planErrors = planDesignActions?.errors ?? [];
  const [isProcedureRunDescriptionInEditMode, setIsProcedureRunDescriptionInEditMode] = useState(false);
  const [isProcedureRunDesignInEditMode, setIsProcedureRunDesignInEditMode] = useState(false);
  const [selectedProcedureRunsForAnalysis, setSelectedProcedureRunsForAnalysis] = useState<
    Array<ProcedureRunSelectionForAnalysis>
  >([]);
  // Design actions published by the run design tab for the header.
  const [runDesignActions, setRunDesignActions] = useState<RunDesignActions | null>(null);
  const refetchStudyExecutionHeader =
    selectedProcedureRun.id != null
      ? procedureRunResult.refetch
      : selectedProcedureRunPlan.id != null
        ? procedureRunPlanResult.refetch
        : props.refetch;

  const addPromotingPlanId = useCallback(
    (planId: string) => {
      dispatch(addPromotingPlanIdAction(studyId, planId));
    },
    [dispatch, studyId]
  );

  const removePromotingPlanId = useCallback(
    (planId: string) => {
      dispatch(removePromotingPlanIdAction(studyId, planId));
    },
    [dispatch, studyId]
  );

  const handlePromotePlan = useCallback(() => {
    const planId = selectedProcedureRunPlan.id;
    if (planId == null) {
      return;
    }

    const procedureRunLabel = getProcedureRunUIStringForStudyType(props.study.studySchema.studyType);
    const createPromotionTrayTask = (): string =>
      TrayTaskActions.createTrayTask({
        heading: (
          <ToastMessage
            body={<>Creating {procedureRunLabel}...</>}
            progress={TrayTaskConstants.INDETERMINATE_PROGRESS}
          />
        ),
        // Hide "native" percentage, since ToastMessage has a better UI when totalTimeMs is provided.
        progress: TrayTaskConstants.HIDE_PROGRESS,
      });

    const refreshAfterPromotion = (creates: Array<{id: string; folderId: string}>): Promise<void> =>
      onSuccessAfterExperimentCreate(props.study.id, {creates}, props.study.studySchema.studyType);

    const navigateToCreatedRun = (creates: Array<{id: string}>): void => {
      const createdRunId = creates[0]?.id ?? undefined;
      navigateToStudyOverviewTab(props.study.id, createdRunId);
    };

    const promotePlan = async (): Promise<void> => {
      if (planDesignActions?.hasUnsavedChanges) {
        await planDesignActions.onSave();
      }

      addPromotingPlanId(planId);
      const baseUnknownErrorAction = getExperimentUnknownErrorAction(
        props.study.id,
        props.study.studySchema.studyType
      );
      const onUnknownError = {
        ...baseUnknownErrorAction,
        onShowErrorToast: () => {
          removePromotingPlanId(planId);
        },
      };
      let shouldClearPromotingId = true;

      // Navigate to study overview immediately so user sees the listing with the greyed out row
      navigateToStudyOverviewTab(props.study.id);

      try {
        const response = await promoteProcedureRunPlans([planId], {supportsAsync: true});
        if (response.async) {
          shouldClearPromotingId = false;
          showExperimentCreationTrayTask(
            response.data.taskId,
            response.data.status_url,
            response.taskMeta,
            [
              {
                studyId: props.study.id,
                studyType: props.study.studySchema.studyType,
                procedureRunPlanId: planId,
              },
            ],
            {
              onSuccess: (result) => {
                refreshAfterPromotion(result.creates);
                removePromotingPlanId(planId);
              },
              onOpen: (result) => {
                navigateToCreatedRun(result.creates);
              },
              onUnknownError,
              onRetry: () => {
                removePromotingPlanId(planId);
                promotePlan();
              },
            },
            response.data.timingMeta
          );
          return;
        }
        refreshAfterPromotion(response.data.creates);
        const trayTaskId = createPromotionTrayTask();
        TrayTaskActions.updateTrayTask(trayTaskId, {
          progress: TrayTaskConstants.SUCCESS_PROGRESS,
          heading: <ToastMessage body={<>Created {procedureRunLabel} successfully.</>} progress={100} />,
          onHeadingClick: () => {
            navigateToCreatedRun(response.data.creates);
          },
        });
      } catch (error) {
        const trayTaskId = createPromotionTrayTask();
        TrayTaskActions.updateTrayTask(trayTaskId, {
          progress: TrayTaskConstants.FAILURE_PROGRESS,
          error: (
            <ToastMessage
              header={`Creating ${procedureRunLabel} failed.`}
              body={notifications.getFriendlyError(error) ?? 'An error occurred.'}
            />
          ),
          actions: [
            {
              actionText: 'Retry...',
              onActionClick: () => {
                promotePlan();
              },
            },
          ],
        });
        throw error;
      } finally {
        if (shouldClearPromotingId) {
          removePromotingPlanId(planId);
        }
      }
    };

    promotePlan().catch(() => undefined);
  }, [
    selectedProcedureRunPlan.id,
    addPromotingPlanId,
    removePromotingPlanId,
    props.study.id,
    props.study.studySchema.studyType,
    planDesignActions,
  ]);

  const openConfirmPromotionModal = useCallback(() => {
    setIsConfirmPromotionModalOpen(true);
  }, []);

  const closeConfirmPromotionModal = useCallback(() => {
    setIsConfirmPromotionModalOpen(false);
  }, []);

  const handleConfirmPromotion = useCallback(() => {
    closeConfirmPromotionModal();
    handlePromotePlan();
  }, [closeConfirmPromotionModal, handlePromotePlan]);

  const onStudyCrumbClick = useCallback(() => {
    setCurrentProcedureRunTab(
      selectedProcedureRunPlan.id != null ? ProcedureRunTab.DESIGN : ProcedureRunTab.OVERVIEW
    );
    navigateToStudyOverviewTab(props.study.id);
  }, [selectedProcedureRunPlan.id, setCurrentProcedureRunTab, props.study.id]);

  const isFlowchartProcedureRunDesignEnabled = shouldShowFlowchartProcedureRunDesign();

  const tabProps = procedureRunViewerTabsProps({
    currentProcedureRunTab,
    setCurrentProcedureRunTab,
    selectedProcedureRunId: selectedProcedureRun?.id ?? null,
    selectedProcedureRunPlanId: selectedProcedureRunPlan?.id ?? null,
  });

  const studyOverviewContextValue = useMemo(
    () => ({
      selectedProcedureRun,
      selectedProcedureRunPlan,
      selectedProcedureRunsForAnalysis,
      currentProcedureRunTab,
      setCurrentProcedureRunTab,
      promotingPlanIds,
      addPromotingPlanId,
      removePromotingPlanId,
      setSelectedProcedureRunsForAnalysis,
      isProcedureRunDescriptionInEditMode,
      setIsProcedureRunDescriptionInEditMode,
      isProcedureRunDesignInEditMode,
      setIsProcedureRunDesignInEditMode,
    }),
    [
      currentProcedureRunTab,
      selectedProcedureRun,
      selectedProcedureRunPlan,
      selectedProcedureRunsForAnalysis,
      setCurrentProcedureRunTab,
      promotingPlanIds,
      addPromotingPlanId,
      removePromotingPlanId,
      isProcedureRunDescriptionInEditMode,
      isProcedureRunDesignInEditMode,
    ]
  );

  const procedureRunPlanContextValue = useMemo(
    () => ({planDesignActions, setPlanDesignActions}),
    [planDesignActions]
  );

  const procedureRunContextValue = useMemo(
    () => ({runDesignActions, setRunDesignActions}),
    [runDesignActions]
  );

  // Reset edit mode when switching procedure runs.
  useEffect(() => {
    setIsProcedureRunDescriptionInEditMode(false);
    setIsProcedureRunDesignInEditMode(false);
  }, [selectedProcedureRun.id]);

  useEffect(() => {
    if (currentProcedureRunTab !== ProcedureRunTab.DESIGN) {
      setIsProcedureRunDesignInEditMode(false);
    }
  }, [currentProcedureRunTab]);

  if (
    procedureRunResult.type === QueryResultType.PENDING ||
    procedureRunPlanResult.type === QueryResultType.PENDING
  ) {
    return <CenteredSpinner />;
  }
  // Legacy header for the flag-off path and for STUDY / RUN_PLAN views. RUN detail uses
  // ProcedureRunHeader + ProcedureRunLayout above.
  const legacyHeader = (
    <StudyExecutionHeader
      {...props}
      refetch={refetchStudyExecutionHeader}
      executionContainerStyling={executionContainer}
      headerSectionContainerStyling={headerSectionContainerStyling}
      title={(selectedProcedureRun.name || selectedProcedureRunPlan.name) ?? undefined}
      headerBreadcrumb={studyOverviewCrumbs({
        selectedProcedureRun,
        selectedProcedureRunPlan,
        studyName: props.study.name,
        setCurrentProcedureRunTab,
        studyId: props.study.id,
      })}
      procedureRunPlanId={selectedProcedureRunPlan.id ?? undefined}
      procedureRunId={selectedProcedureRun.id ?? undefined}
      procedureRunOrPlanCreatedAt={
        fetchedProcedureRun != null
          ? fetchedProcedureRun.createdAt
          : fetchedProcedureRunPlan != null
            ? fetchedProcedureRunPlan.createdAt
            : undefined
      }
      procedureRunDescription={fetchedProcedureRun?.description ?? undefined}
      procedureRunPlanDescription={fetchedProcedureRunPlan?.description ?? undefined}
      headerButtons={{
        buttons:
          selectedProcedureRun.id != null || selectedProcedureRunPlan.id != null ? (
            getProcedureRunOrPlanViewButtons({
              study: props.study,
              selectedProcedureRunId: selectedProcedureRun.id ?? undefined,
              selectedProcedureRunPlanId:
                selectedProcedureRunPlan.id ?? procedureRunPlanIdFromRun ?? undefined,
              planOrRunName: (selectedProcedureRun.name || selectedProcedureRunPlan.name) ?? '',
              onLinksSaved: props.refetch,
              onOpenPromotePlanConfirmation: openConfirmPromotionModal,
              planErrors,
              hasUnsavedPlanChanges,
              shouldDisableCreate: selectedProcedureRunPlan.procedureId == null,
              isProcedureRunArchived: fetchedProcedureRun?.archiveData.archived ?? false,
            })
          ) : (
            <>
              <AnalyzeStudyButton
                study={{
                  id: props.study.id,
                  name: props.study.name,
                  humanId: props.study.humanId,
                }}
                studyType={props.study.studySchema.studyType}
                isStudyArchived={props.study.archiveRecord != null}
                hasProcedureRuns={accessibleObjs(props.study.procedureRuns).length > 0}
                selectedProcedureRuns={selectedProcedureRunsForAnalysis}
                procedureType={getProcedureTypeForStudyType(props.study.studySchema.studyType)}
                actionOrigin={ActionOrigin.STUDY_OVERVIEW_PAGE}
              />
              <SetStudyPhaseDropdown
                study={{
                  id: props.study.id,
                  phase: props.study.phase,
                  studyType: props.study.studySchema.studyType,
                }}
                refetch={props.refetch}
                isDisabled={!props.isEditable}
                requiresApproval={props.isDesignApprovalRequiredToProceed}
                buttonSize={ButtonSize.MEDIUM}
                allowReturnToDesignPhase={props.allowReturnToDesignPhase}
              />
              <AddProcedureRunPlanButton
                studyId={props.study.id}
                studyType={props.study.studySchema.studyType}
              />
            </>
          ),
        buttonsStyling: buttonStyling,
      }}
      {...tabProps}
    />
  );

  // The body renders from props.execution, created by StudyOverview and passed through unchanged, so
  // local state updates here do not re-render it.
  return (
    <div
      className={css(styles.overviewContainer)}
      {...getTestProps({component: 'MultiProcedureRunOverviewTabContents', element: 'overview'})}
    >
      <StudyOverviewContext.Provider value={studyOverviewContextValue}>
        <ProcedureRunPlanContext.Provider value={procedureRunPlanContextValue}>
          <ProcedureRunContext.Provider value={procedureRunContextValue}>
            {isConfirmPromotionModalOpen && selectedProcedureRunPlan.procedureId != null && (
              <ConfirmPromotionModal
                studyType={props.study.studySchema.studyType}
                procedureId={selectedProcedureRunPlan.procedureId}
                onRequestClose={closeConfirmPromotionModal}
                onRequestSubmit={() => {
                  handleConfirmPromotion();
                }}
              />
            )}
            {isFlowchartProcedureRunDesignEnabled && selectedProcedureRun.id != null ? (
              <ProcedureRunLayout
                header={
                  <ProcedureRunHeader
                    study={props.study}
                    procedureRunId={selectedProcedureRun.id}
                    procedureRunName={selectedProcedureRun.name ?? ''}
                    procedureRunPlanId={procedureRunPlanIdFromRun ?? undefined}
                    procedureRunDescription={fetchedProcedureRun?.description ?? undefined}
                    procedureRunCreatedAt={fetchedProcedureRun?.createdAt}
                    isProcedureRunArchived={fetchedProcedureRun?.archiveData.archived ?? false}
                    refetch={refetchStudyExecutionHeader}
                    onStudyCrumbClick={onStudyCrumbClick}
                  />
                }
                tabProps={tabProps}
                execution={props.execution}
              />
            ) : isFlowchartProcedureRunDesignEnabled && selectedProcedureRunPlan.id != null ? (
              <ProcedureRunLayout
                header={
                  <ProcedureRunPlanHeader
                    study={props.study}
                    procedureRunPlanId={selectedProcedureRunPlan.id}
                    procedureRunPlanName={selectedProcedureRunPlan.name ?? ''}
                    procedureRunPlanDescription={fetchedProcedureRunPlan?.description ?? undefined}
                    procedureRunPlanCreatedAt={fetchedProcedureRunPlan?.createdAt}
                    isEditable={props.isEditable}
                    shouldDisableCreate={selectedProcedureRunPlan.procedureId == null}
                    onCreateRun={openConfirmPromotionModal}
                    onLinksSaved={props.refetch}
                    refetch={refetchStudyExecutionHeader}
                    onStudyCrumbClick={onStudyCrumbClick}
                  />
                }
                tabProps={tabProps}
                execution={props.execution}
              />
            ) : (
              legacyHeader
            )}
          </ProcedureRunContext.Provider>
        </ProcedureRunPlanContext.Provider>
      </StudyOverviewContext.Provider>
    </div>
  );
}

const styles = StyleSheet.create({
  overviewContainer: {
    height: '100%',
    width: '100%',
  },
  breadcrumbsFont: {
    fontSize: 13,
    color: Colors.GRAY_DARKER,
  },
  headerDivider: {
    borderBottom: `1.5px solid ${Colors.GRAY_LIGHT}`,
  },
  tab: {
    ...FONT_PROXIMA_12,
  },
  tabContainer: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    color: Colors.GRAY_DARKER,
    marginTop: 6,
    minHeight: 0,
  },
  tabList: {
    marginLeft: '12px',
  },
  tabPanel: {
    marginTop: 0,
  },
});

const executionContainer: React.CSSProperties = {
  flex: '1 0',
};

const buttonStyling: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: 8,
};

const headerSectionContainerStyling: React.CSSProperties = {
  margin: '0 12px 4px',
};
